const PRODUCT_IMAGE_UPLOAD_LIMIT_BYTES = 4 * 1024 * 1024;
const PRODUCT_IMAGE_MAX_WIDTH = 1000;
const PRODUCT_IMAGE_MAX_HEIGHT = 1333;
const PRODUCT_IMAGE_WEBP_QUALITY = 0.72;
const SUPPORTED_PRODUCT_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export const PRODUCT_IMAGE_INPUT_ACCEPT = "image/jpeg,image/png,image/webp";
export const PRODUCT_IMAGE_HELP_TEXT = "Max 4MB. JPG, PNG, WebP. Images are optimized to WebP before upload.";
export const PRODUCT_IMAGE_TOO_LARGE_MESSAGE =
  "Image is too large. Please choose a JPG, PNG, or WebP file under 4MB.";
export const PRODUCT_IMAGE_INVALID_TYPE_MESSAGE =
  "Please select a JPG, PNG, or WebP image.";

function isSupportedProductImageType(contentType: string): boolean {
  return SUPPORTED_PRODUCT_IMAGE_TYPES.has(contentType);
}

export function validateProductImageFile(file: File): string | null {
  if (!isSupportedProductImageType(file.type)) {
    return PRODUCT_IMAGE_INVALID_TYPE_MESSAGE;
  }

  if (file.size > PRODUCT_IMAGE_UPLOAD_LIMIT_BYTES) {
    return PRODUCT_IMAGE_TOO_LARGE_MESSAGE;
  }

  return null;
}

function getOptimizedDimensions(width: number, height: number): { width: number; height: number } {
  const scale = Math.min(
    1,
    PRODUCT_IMAGE_MAX_WIDTH / width,
    PRODUCT_IMAGE_MAX_HEIGHT / height,
  );

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function replaceExtensionWithWebp(filename: string): string {
  const trimmed = filename.trim();
  const withoutExtension = trimmed.replace(/\.[^.]+$/, "");
  return `${withoutExtension || "product-image"}.webp`;
}

async function canvasToWebpBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Unable to optimize product image."));
          return;
        }

        resolve(blob);
      },
      "image/webp",
      PRODUCT_IMAGE_WEBP_QUALITY,
    );
  });
}

async function optimizeProductImage(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);

  try {
    const { width, height } = getOptimizedDimensions(bitmap.width, bitmap.height);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Unable to optimize product image.");
    }

    context.drawImage(bitmap, 0, 0, width, height);
    return await canvasToWebpBlob(canvas);
  } finally {
    bitmap.close();
  }
}

export async function prepareProductImageForUpload(file: File): Promise<{
  fileData: ArrayBuffer;
  filename: string;
  contentType: string;
  size: number;
}> {
  const validationError = validateProductImageFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  const optimizedBlob = await optimizeProductImage(file);

  if (optimizedBlob.size > PRODUCT_IMAGE_UPLOAD_LIMIT_BYTES) {
    throw new Error(PRODUCT_IMAGE_TOO_LARGE_MESSAGE);
  }

  return {
    fileData: await optimizedBlob.arrayBuffer(),
    filename: replaceExtensionWithWebp(file.name),
    contentType: "image/webp",
    size: optimizedBlob.size,
  };
}

